import { createStore } from 'vuex'
import { createToast } from "mosha-vue-toastify";
import "mosha-vue-toastify/dist/style.css";
import axios from "axios";

function normalizeApiError(error, fallbackMessage = "Request failed.") {
	if (error && error.response && error.response.data) {
		if (!error.response.data.errors) {
			error.response.data.errors = {};
		}
		return error;
	}

	const message = error?.message || fallbackMessage;

	return {
		...error,
		response: {
			status: error?.response?.status || 0,
			data: {
				message,
				errors: {},
			},
		},
	};
}

function isSessionExpiredError(error) {
	const status = error?.response?.status;
	const message = error?.response?.data?.message;
	const code = error?.response?.data?.code;

	return (
		status === 401 ||
		status === 419 ||
		status === 440 ||
		message === "Unauthenticated." ||
		message === "Session expired due to inactivity." ||
		code === 440
	);
}



export default createStore({
	state: {
		// Production (path-based on same server): use relative URLs so it works with public IP, private IP, or domain
		//apiUrl: "http://52.66.40.36/rs/api/",
		//download_url: "http://52.66.40.36/rs/",
		// Other options (uncomment if needed):
		 apiUrl: "https://rs.hassan.patterneffects.in/api/",
		 download_url: "https://rs.hassan.patterneffects.in/",
		// apiUrl: "http://localhost:8000/api/",
		// download_url: "http://localhost:8000/",
		user: null,
		token: "",
		permissions: [],
		isLoggingOut: false
	},
	getters: {
		user(state) {
			return state.user;
		},
		token(state) {
			return state.token;
		},
		downloadUrl(state) {
			return state.download_url;
		},
		permissions(state) {
			return state.permissions;
		},
		isLoggingOut(state) {
			return state.isLoggingOut;
		},
	},
	mutations: {
		setUser(state, user) {
			state.user = user;
		},
		setToken(state, token) {
			state.token = token;
		},
		setPermissions(state, permissions) {
			state.permissions = Array.isArray(permissions) ? permissions : [];
		},
		setLoggingOut(state, value) {
			state.isLoggingOut = !!value;
		},
	},
	actions: {
		async setUser(context, payload) {
			sessionStorage.setItem("user", JSON.stringify(payload))
			context.commit('setUser', payload);
		},
		async setToken(context, payload) {
			sessionStorage.setItem("token", payload)
			context.commit('setToken', payload);
		},
		async logout(context) {
			sessionStorage.removeItem('user');
			sessionStorage.removeItem('token');
			sessionStorage.removeItem('permissions');
			context.commit('setUser', null);
			context.commit('setToken', "");
			context.commit('setPermissions', []);
		},

		async beginLogoutRedirect(context) {
			if (context.state.isLoggingOut) return;
			context.commit('setLoggingOut', true);
			await context.dispatch('logout');
			const currentBase = window.location.href.split('#')[0];
			const fallbackBase = (context.state.download_url || `${window.location.origin}/`).replace(/\/?$/, '/');
			const basePath = (currentBase || fallbackBase).replace(/\/?$/, '/');
			window.location.assign(`${basePath}?logoutRedirect=${Date.now()}#/`);
		},

		async auth(context, payload) {
			if (context.state.isLoggingOut) {
				const err = normalizeApiError({ message: "Skipped API call during logout." });
				err.__duringLogout = true;
				return Promise.reject(err);
			}
			try {
				return await axios.post(context.state.apiUrl + payload.uri, payload.data);
			} catch (error) {
				const normalized = normalizeApiError(error);
				if (isSessionExpiredError(normalized)) {
					await context.dispatch('beginLogoutRedirect');
					normalized.__duringLogout = true;
				}
				return Promise.reject(normalized);
			}
		},

		async post(context, payload) {
			if (context.state.isLoggingOut) {
				const err = normalizeApiError({ message: "Skipped API call during logout." });
				err.__duringLogout = true;
				return Promise.reject(err);
			}
			try {
				return await axios.post(context.state.apiUrl + payload.uri, payload.data, {
					headers: {
						Authorization: `Bearer ${context.getters.token}`
					}
				});
			} catch (error) {
				const normalized = normalizeApiError(error);
				if (isSessionExpiredError(normalized)) {
					await context.dispatch('beginLogoutRedirect');
					normalized.__duringLogout = true;
				}
				return Promise.reject(normalized);
			}
		},

		async fileDownload(context, payload) {
			if (context.state.isLoggingOut) {
				const err = normalizeApiError({ message: "Skipped API call during logout." });
				err.__duringLogout = true;
				return Promise.reject(err);
			}
			try {
				return await axios.post(context.state.apiUrl + payload.uri, payload.data, {
					responseType: payload.responseType || 'blob',
					headers: {
						Authorization: `Bearer ${context.getters.token}`
					}
				});
			} catch (error) {
				const normalized = normalizeApiError(error);
				if (isSessionExpiredError(normalized)) {
					await context.dispatch('beginLogoutRedirect');
					normalized.__duringLogout = true;
				}
				return Promise.reject(normalized);
			}
		},

		async adminCheck(context, payload) {
			return context.dispatch('post', payload);
		},

		async error(context, description) {
			if (context.state.isLoggingOut) return;
			await createToast(
				{
					title: "Error",
					description: description || "The given data was invalid.",
				},
				{
					showIcon: true,
					hideProgressBar: true,
					type: "danger",
					position: "top-right",
				}
			);
		},

		async success(context, description) {
			await createToast(
				{
					title: "Success",
					description: description || "Data is successfully submitted.",
				},
				{
					showIcon: true,
					hideProgressBar: true,
					type: "success",
					position: "top-right",
				}
			);
		},

		async info(context, description) {
			await createToast(
				{
					title: "Info",
					description: description || "Not enough input data",
				},
				{
					showIcon: true,
					hideProgressBar: true,
					type: "info",
					position: "top-right",
				}
			);
		},
		async setPermissions(context, payload) {
			await context.commit('setPermissions', payload);
		},
	}
})
